import React from 'react'
import { View, TouchableOpacity, StyleSheet } from 'react-native'
import { useTheme } from '@react-navigation/native'
import { Icon, Text, IconName, NotifBadge } from '@components'
import { Scope } from '@utils'
import { useLanguage } from '@context'
import { Theme } from '@theme'
import { useListingContactsStore } from '@store'
import { useScreen } from '@hooks'

interface ContactFiltersProps {
  noScope?: boolean
  scope?: Scope[]
  noMarginTop?: boolean
  onScopeChange?: (scope: Scope[]) => void
  onFilterPress?: () => void
  activeFiltersCount?: number
  hasActiveFilters?: boolean
}

export const ContactFilters: React.FC<ContactFiltersProps> = ({
  noScope = false,
  scope = [],
  onScopeChange,
  onFilterPress,
  noMarginTop,
}) => {
  const theme = useTheme()
  const { t } = useLanguage()
  const { isDesktop } = useScreen()
  const styles = getStyle(theme, isDesktop)

  const { getActiveFiltersCount } = useListingContactsStore()

  // Fonction pour obtenir les couleurs spécifiques de chaque scope
  const getScopeColors = (scopeType: Scope) => {
    switch (scopeType) {
      case Scope.PRIVATE:
        return {
          activeColor: theme.colors.green,
          activeBgColor: theme.colors.green + '20', // Vert avec transparence
          activeTextColor: theme.colors.green,
        }
      case Scope.PROFESSIONAL:
        return {
          activeColor: theme.colors.primary,
          activeBgColor: theme.colors.primary + '20', // Bleu avec transparence
          activeTextColor: theme.colors.primary,
        }
      case Scope.GHOST:
        return {
          activeColor: theme.colors.purple,
          activeBgColor: theme.colors.purple + '20', // Violet avec transparence
          activeTextColor: theme.colors.purple,
        }
      default:
        return {
          activeColor: theme.colors.primary,
          activeBgColor: theme.colors.primary + '20',
          activeTextColor: theme.colors.primary,
        }
    }
  }

  const renderScopeButton = (
    scopeType: Scope,
    iconName: IconName,
    translationKey: string,
    showText: boolean = true,
  ) => {
    const isActive = scope.includes(scopeType)
    const colors = getScopeColors(scopeType)

    return (
      <TouchableOpacity
        testID={`scope-${scopeType.toLowerCase()}`}
        style={[
          styles.filterOption,
          isActive && {
            //backgroundColor: colors.activeBgColor,
            borderColor: colors.activeColor,
          },
        ]}
        onPress={() => {
          let newScope = scope
          // If toggling PRIVATE or PROFESSIONAL, remove the other if present
          if (scopeType === Scope.PRIVATE) {
            if (scope.includes(Scope.PRIVATE)) {
              // Remove PRIVATE
              newScope = scope.filter((s) => s !== Scope.PRIVATE)
            } else {
              // Add PRIVATE, remove PROFESSIONAL if present
              newScope = [...scope.filter((s) => s !== Scope.PROFESSIONAL), Scope.PRIVATE]
            }
          } else if (scopeType === Scope.PROFESSIONAL) {
            if (scope.includes(Scope.PROFESSIONAL)) {
              // Remove PROFESSIONAL
              newScope = scope.filter((s) => s !== Scope.PROFESSIONAL)
            } else {
              // Add PROFESSIONAL, remove PRIVATE if present
              newScope = [...scope.filter((s) => s !== Scope.PRIVATE), Scope.PROFESSIONAL]
            }
          } else if (scopeType === Scope.GHOST) {
            // For all other scopes, toggle as usual
            newScope = scope.includes(scopeType)
              ? scope.filter((s) => s !== scopeType)
              : [...scope, scopeType]
          }
          onScopeChange?.(newScope)
        }}
      >
        <Icon
          name={iconName}
          size={16}
          color={isActive ? colors.activeTextColor : theme.colors.greyBlue}
          style={showText ? styles.tabIcon : undefined}
        />
        {showText && (
          <Text
            type={isActive ? 'bodyBold' : 'body'}
            style={isActive ? { color: colors.activeTextColor } : styles.tabButtonText}
          >
            {t(translationKey)}
          </Text>
        )}
      </TouchableOpacity>
    )
  }

  return (
    <View style={[styles.filterContainer, noMarginTop && styles.noMarginTop]}>
      {!noScope && (
        <View style={styles.filterButtons}>
          {renderScopeButton(Scope.PRIVATE, 'lock', 'contactType.PRIVATE')}
          {renderScopeButton(Scope.PROFESSIONAL, 'work', 'contactType.PROFESSIONAL')}
          {renderScopeButton(Scope.GHOST, 'ghost', 'contactType.GHOST', false)}
        </View>
      )}

      {!!onFilterPress && (
        <TouchableOpacity style={styles.filterButton} onPress={onFilterPress}>
          <Icon name="filter2" size={16} color={theme.colors.white} />
          <NotifBadge total={getActiveFiltersCount()} />
        </TouchableOpacity>
      )}
    </View>
  )
}

const getStyle = (theme: Theme, isDesktop: boolean) =>
  StyleSheet.create({
    filterContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingHorizontal: 16,
      paddingBottom: isDesktop ? 0 : 10,
      gap: theme.spacing.sm,
      justifyContent: 'flex-start',
      flexShrink: 0,
    },
    noMarginTop: {
      marginTop: 0,
    },
    filterButtons: {
      flexDirection: 'row',
      flex: isDesktop ? undefined : 1,
      alignItems: 'center',
      gap: theme.spacing.sm,
    },
    filterOption: {
      alignItems: 'center',
      paddingHorizontal: theme.spacing.md,
      borderRadius: 20,
      borderWidth: 1,
      borderColor: theme.colors.lightGrey,
      backgroundColor: theme.colors.white,
      flexDirection: 'row',
      justifyContent: 'center',
      height: 36,
    },
    tabIcon: {
      marginRight: theme.spacing.xs,
    },
    tabButtonText: {
      color: theme.colors.greyBlue,
    },
    filterButton: {
      flexDirection: 'row',
      gap: 6,
      paddingVertical: theme.spacing.xs * 2,
      paddingHorizontal: theme.spacing.md,
      marginLeft: isDesktop ? theme.spacing.md : 0,
      backgroundColor: theme.colors.primary,
      borderRadius: theme.border.jumbo,
      height: 36,
      justifyContent: 'center',
      alignItems: 'center',
    },
  })
